import request from 'graphql-request'
import { LIVE_CHECK_REFUSAL_PREFIXES } from 'lib/liveLoginCheckCopy'

const {
  VITE_GQL_ENDPOINT: appsyncUrl
} = import.meta.env

const gqlUrl = appsyncUrl as string + '/graphql'

export const GENERIC_ERROR = 'Something went wrong. Please try again.'

const rawErrorMessage = (err: unknown): string => {
  const resp = (err as { response?: { errors?: Array<{ message?: unknown }> } })?.response
  const gqlMsg = resp?.errors?.[0]?.message
  if (typeof gqlMsg === 'string' && gqlMsg.length > 0) return gqlMsg
  if (err instanceof Error && typeof err.message === 'string') return err.message
  if (typeof err === 'string') return err
  return ''
}

export const BOT_NAME_PLACEHOLDER = 'your-bot-username'

export const withBotName = (message: string, botLogin: string | null): string =>
  botLogin === null || botLogin === ''
    ? message
    : message.split(BOT_NAME_PLACEHOLDER).join(botLogin)

const EDGE_REFUSAL_PREFIXES: string[] = [
  'Connect your Twitch account on the Connections page',
  'Your Twitch connection needs updated permissions',
  'You can add a channel only if',
  "You're not a moderator of ",
  ...LIVE_CHECK_REFUSAL_PREFIXES
]

const isEdgeRefusal = (raw: string): boolean =>
  EDGE_REFUSAL_PREFIXES.some((prefix) => raw.startsWith(prefix))

const streamChannelLogins = new Map<string, string>()

export const setStreamChannelLogins = (
  channels: Array<{ broadcasterUserId: string, channelLogin: string }>
): void => {
  streamChannelLogins.clear()
  for (const channel of channels) {
    const id = channel?.broadcasterUserId
    const login = channel?.channelLogin
    if (typeof id === 'string' && id !== '' && typeof login === 'string' && login !== '') {
      streamChannelLogins.set(id, login)
    }
  }
}

export const clearStreamChannelLogins = (): void => {
  streamChannelLogins.clear()
}

const CLASH_REFUSAL = /already running another (trivia question|giveaway|camera control): ([^.]*)\./i

const joinChannelNames = (names: string[]): string =>
  names.length === 1
    ? names[0]
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`

const clashRefusalCopy = (raw: string): string | null => {
  const match = CLASH_REFUSAL.exec(raw)
  if (match === null) return null
  const names: string[] = []
  for (const part of match[2].split(',')) {
    const login = streamChannelLogins.get(part.trim())
    if (login !== undefined && !names.includes(login)) names.push(login)
  }
  const place = names.length > 0 ? joinChannelNames(names) : "one of this stream's channels"
  switch (match[1].toLowerCase()) {
    case 'trivia question':
      return `Another trivia question is already taking answers on ${place}. Stop it first, then start this one.`
    case 'giveaway':
      return `Another giveaway is already taking entries on ${place}. Stop it first, then start this one.`
    case 'camera control':
      return `Another camera control is already listening on ${place}. Stop it first, then start this one.`
    default:
      return null
  }
}

export const ELEMENTS_PER_STREAM_COPY =
  'This stream has reached its plan\'s limit on elements. Polls, tug of wars, scoreboards, trivia questions, chat overlays and emote walls all count. Delete one first, or ask the host to upgrade.'

const ERROR_RULES: Array<{ test: RegExp, message: string }> = [
  {
    test: /Plan limit reached: interactiveElementsPerStream\b/,
    message: ELEMENTS_PER_STREAM_COPY
  },
  {
    test: /already running another (poll|tug)/i,
    message: 'Another poll or tug-of-war is already live on that channel. Stop it first, then start this one.'
  },
  {
    test: /create subscription failed|missing proper authorization|moderator/i,
    message: `That channel couldn’t be added. Most often the bot just needs to be a moderator there — type “/mod ${BOT_NAME_PLACEHOLDER}” in the channel’s Twitch chat (or add it under the channel’s Roles). If the bot is already a mod, its Twitch authorization may have been revoked — reconnect it on the Bots page, then try again.`
  },
  {
    test: /insufficient permission|not authorized|unauthorized|forbidden|permission|requires (admin|owner|editor)/i,
    message: 'You don’t have permission to do that.'
  },
  {
    test: /auth0|access_denied|\bm2m\b|service not enabled|user directory|token fetch/i,
    message: GENERIC_ERROR
  }
]

const SENSITIVE_MARKERS = /auth0|access_denied|\bm2m\b|bearer\b|secret|https?:\/\/|\bat [\w.$]+ \([^)]*:\d+:\d+\)/i

export const humanizeError = (err: unknown, fallback: string = GENERIC_ERROR): string => {
  const raw = rawErrorMessage(err).trim()
  if (isEdgeRefusal(raw)) return raw
  const clash = clashRefusalCopy(raw)
  if (clash !== null) return clash
  for (const rule of ERROR_RULES) {
    if (rule.test.test(raw)) return rule.message
  }
  if (raw === '' || raw.length > 200 || SENSITIVE_MARKERS.test(raw)) return fallback
  return raw
}

const SAFE_ERROR_NAME = /^[A-Za-z][A-Za-z0-9:._-]{0,63}$/

const safeErrorName = (value: unknown): string =>
  typeof value === 'string' && SAFE_ERROR_NAME.test(value) ? value : 'unnamed'

const failureSummary = (err: unknown): { name: string, status: number | null, errorTypes: string[] } => {
  const response = (err as { response?: { status?: unknown, errors?: unknown } } | null)?.response
  const errors: unknown[] = Array.isArray(response?.errors) ? response.errors : []
  return {
    name: safeErrorName((err as { name?: unknown } | null)?.name),
    status: typeof response?.status === 'number' ? response.status : null,
    errorTypes: errors.slice(0, 5).map((e) => safeErrorName((e as { errorType?: unknown } | null)?.errorType))
  }
}

export const graphqlRequest = async <T>(
  query: string,
  variables: Record<string, any>,
  token: string
): Promise<T> => {
  try {
    return await request<T>(gqlUrl, query, variables, {
      Authorization: token
    })
  } catch (err) {
    console.error('[graphqlRequest] request failed', failureSummary(err))
    throw new Error(humanizeError(err))
  }
}

export const formatLocalDateTime = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short'
  })

export const getInitials = (source: string | null | undefined): string => {
  if (source == null) return 'U?'
  const trimmed = source.trim()
  if (trimmed === '') return 'U?'
  const parts = trimmed.split(/\s+/).filter((p) => p.length > 0)
  if (parts.length >= 2) {
    const first = parts[0].replace(/[^A-Za-z0-9]/g, '')
    const last = parts[parts.length - 1].replace(/[^A-Za-z0-9]/g, '')
    const a = first.charAt(0)
    const b = last.charAt(0)
    const combined = `${a}${b}`
    if (combined.length === 2) return combined.toUpperCase()
  }
  const cleaned = trimmed.replace(/[^A-Za-z0-9]/g, '')
  if (cleaned.length === 0) return 'U?'
  return cleaned.slice(0, 2).toUpperCase()
}
